import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    IconButton,
    Slider,
    Stack,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import ReactJson from '@microlink/react-json-view';
import type { ChatSession } from '@lib/models/ChatSession';
import type { Interaction } from '@lib/models/Interaction';
import { useAiChatSessionStore } from '../AiChatContext';

interface SessionDataDialogProps {
    open: boolean;
    onClose: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`session-data-tabpanel-${index}`}
            aria-labelledby={`session-data-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `session-data-tab-${index}`,
        'aria-controls': `session-data-tabpanel-${index}`,
    };
}

const SessionDataDialog: React.FC<SessionDataDialogProps> = ({ open, onClose }) => {
    const currentSession = useAiChatSessionStore((state) => state.session);
    const setSession = useAiChatSessionStore((state) => state.setSession);

    const [tabValue, setTabValue] = useState(0);
    const [editedSession, setEditedSession] = useState<ChatSession | null>(null);
    const [indentation, setIndentation] = useState(1);

    // Reset edited state whenever the dialog opens so it always shows live data
    useEffect(() => {
        if (open) {
            setEditedSession(null);
        }
    }, [open]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleJsonEdit = (edit: any) => {
        setEditedSession(edit.updated_src as ChatSession);
    };

    const handleClose = () => {
        if (editedSession) {
            setSession({ ...editedSession, interactions: [...(editedSession.interactions ?? [])] });
        }
        setEditedSession(null);
        onClose();
    };

    const formatInteractionsAsText = (interactions: Interaction[]): string => {
        if (!interactions) return "";

        return interactions.map((interaction, index) => {
            const question = interaction.interactionQuestion || 'No question available';
            const response = interaction.parts
                .filter(part => part.text)
                .map(part => part.text)
                .join('\n\n');

            return `------ Interaction ${index + 1} ---------
Question: ${question}
Response:

${response || 'No response available'}

`;
        }).join('\n');
    };

    const sessionToDisplay = editedSession || currentSession;

    if (!sessionToDisplay) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Session Data
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2, color: 'text.secondary' }}>
                        No session data available. Please start a conversation first.
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    function handleSliderChange(_event: Event, value: number, _activeThumb: number): void {
        setIndentation(value);
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                }
            }}
        >
            <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                Session Data Viewer
                {editedSession && (
                    <Typography component="span" variant="caption" sx={{ ml: 2, color: 'warning.main' }}>
                        (locally edited)
                    </Typography>
                )}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'rgba(255, 255, 255, 0.6)',
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="session data tabs"
                    sx={{
                        '& .MuiTab-root': { color: 'rgba(255, 255, 255, 0.6)' },
                        '& .Mui-selected': { color: '#ffffff !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1976d2' },
                    }}
                >
                    <Tab label="JSON Visualization" {...a11yProps(0)} />
                    <Tab label="Text Only" {...a11yProps(1)} />
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ height: '50vh', overflow: 'auto' }}>
                        <ReactJson
                            src={sessionToDisplay}
                            theme="monokai"
                            name="session"
                            collapsed={indentation}
                            displayDataTypes={false}
                            displayObjectSize={true}
                            enableClipboard={true}
                            onEdit={handleJsonEdit}
                            onAdd={handleJsonEdit}
                            onDelete={handleJsonEdit}
                            style={{
                                backgroundColor: 'transparent',
                                fontSize: '12px',
                            }}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Box
                        sx={{
                            height: '50vh',
                            overflow: 'auto',
                            backgroundColor: '#2d2d2d',
                            p: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            whiteSpace: 'pre-wrap',
                            color: '#ffffff',
                        }}
                    >
                        {formatInteractionsAsText(sessionToDisplay.interactions)}
                    </Box>
                </TabPanel>
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 1, pt: 0.5 }}>
                <Box sx={{ width: '100%', top: '-5px', position: 'relative' }}>
                    <Stack direction="row" spacing={2} alignItems="center"
                        sx={{
                            width: '30%',
                            height: '60px',
                            '& .MuiSlider-mark': { m: 0 },
                        }}
                    >
                        <Typography>Indentation:</Typography>
                        <Slider
                            value={indentation}
                            onChange={handleSliderChange}
                            min={0}
                            max={7}
                            step={1}
                            marks={Array.from({ length: 8 }, (_, i) => ({
                                value: i,
                                label: i.toString(),
                            }))}
                            sx={{
                                color: "white",
                                height: 4,
                                mb: 30,
                                "& .MuiSlider-thumb": {
                                    width: 16,
                                    height: 16,
                                    backgroundColor: "white",
                                },
                                "& .MuiSlider-track": { backgroundColor: "white" },
                                "& .MuiSlider-rail": { backgroundColor: "rgba(255, 255, 255, 0.3)" },
                            }}
                        />
                    </Stack>
                </Box>
                {editedSession && (
                    <Button
                        size="medium"
                        onClick={() => setEditedSession(null)}
                        variant="outlined"
                        sx={{ mr: 1, minWidth: '180px', overflow: 'visible', textWrap: 'nowrap', p: 1 }}
                    >
                        Reset to Live
                    </Button>
                )}
                <Button onClick={handleClose} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionDataDialog;
